import { isSafeUrl } from './url.js';

/**
 * Zero-dependency rich-text sanitiser.
 *
 * WHY NOT DOMPurify: the IIFE bundle has an 8 kB gzipped budget and the package
 * must ship zero runtime dependencies, so a general-purpose sanitiser (roughly
 * 20 kB gzipped on its own) is out of reach. This implementation is deliberately
 * narrow: it only has to survive the fixed allowlist shared with the server.
 *
 * WHY IT IS STILL SAFE: the API sanitises rich text on save with `sanitize-html`
 * against the very same allowlist, so this pass is defence in depth against a
 * compromised or misconfigured API, a stale cache, or a tampered response. It
 * parses into an inert document (`DOMParser` with `text/html` neither executes
 * scripts nor fetches sub-resources) and rebuilds a fresh tree, so nothing from
 * the untrusted markup is ever attached to the live document.
 */

const ALLOWLIST = __SDK_ALLOWLIST__;

const ALLOWED_TAGS = new Set(ALLOWLIST.tags);
const DROP_WITH_CONTENT = new Set(ALLOWLIST.dropWithContent);
const GLOBAL_ATTRIBUTES = new Set(ALLOWLIST.globalAttributes);
const URL_ATTRIBUTES = new Set(ALLOWLIST.urlAttributes);
const TAG_ATTRIBUTES = ALLOWLIST.tagAttributes;
const FORCE_REL = ALLOWLIST.forceRelOnTargetBlank;

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

function copyAttributes(source: Element, target: Element, tag: string): void {
  const perTag = TAG_ATTRIBUTES[tag];
  const attributes = source.attributes;
  for (let i = 0; i < attributes.length; i += 1) {
    const attribute = attributes[i];
    if (!attribute) continue;
    const name = attribute.name.toLowerCase();

    // Event handlers and inline styles are never negotiable.
    if (name.indexOf('on') === 0 || name === 'style') continue;
    if (!GLOBAL_ATTRIBUTES.has(name) && (!perTag || perTag.indexOf(name) === -1)) continue;

    const value = attribute.value;
    if (URL_ATTRIBUTES.has(name) && !isSafeUrl(value, { allowData: name === 'src' })) continue;

    target.setAttribute(name, value);
  }

  // Reverse tabnabbing protection: an opener-carrying `_blank` link is a hole
  // even when the href itself is harmless.
  if (target.getAttribute('target') === '_blank') target.setAttribute('rel', FORCE_REL);
}

function transfer(source: Node, target: Node, doc: Document): void {
  let child = source.firstChild;
  while (child) {
    const next = child.nextSibling;

    if (child.nodeType === TEXT_NODE) {
      target.appendChild(doc.createTextNode(child.nodeValue ?? ''));
    } else if (child.nodeType === ELEMENT_NODE) {
      const element = child as Element;
      const tag = element.tagName.toLowerCase();

      if (DROP_WITH_CONTENT.has(tag)) {
        // Dropped outright: the contents of a <script> or <style> are payload,
        // not prose, so unwrapping them would leak code as visible text.
      } else if (ALLOWED_TAGS.has(tag)) {
        const clean = doc.createElement(tag);
        copyAttributes(element, clean, tag);
        transfer(element, clean, doc);
        target.appendChild(clean);
      } else {
        // Unknown but harmless wrapper: keep the words, discard the element.
        transfer(element, target, doc);
      }
    }
    // Comments, CDATA and processing instructions are discarded.

    child = next;
  }
}

/**
 * Parses untrusted HTML and returns a fragment containing only allowlisted
 * elements and attributes. Never returns a string, so callers cannot be tempted
 * back into `innerHTML`.
 */
export function sanitizeHtml(html: unknown): DocumentFragment {
  const doc = new DOMParser().parseFromString(`<body>${typeof html === 'string' ? html : ''}`, 'text/html');
  const fragment = doc.createDocumentFragment();
  transfer(doc.body, fragment, doc);
  return fragment;
}
